import { Injectable, Injector } from '@angular/core';
import { PlatformLocation, registerLocaleData } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment-timezone';
import { filter as _filter, merge as _merge } from 'lodash-es';
import { AppConsts } from '@shared/AppConsts';
import { AppSessionService } from '@shared/session/app-session.service';
import { environment } from './environments/environment';
import { AccountServiceProxy, IsTenantAvailableInput, IsTenantAvailableOutput, TenantAvailabilityState } from '@shared/service-proxies/service-proxies';
import { SubdomainTenantResolver } from '@shared/multi-tenancy/tenant-resolvers/subdomain-tenant-resolver';

@Injectable({
  providedIn: 'root',
})
export class AppInitializer {
  constructor(
    private _injector: Injector,
    private _platformLocation: PlatformLocation,
    private _httpClient: HttpClient
  ) { }

  init(): () => Promise<boolean> {
    return () => {
      abp.ui.setBusy();
      return new Promise<boolean>((resolve, reject) => {
        const rejectInitialization = (err: any) => {
          abp.ui.clearBusy();
          reject(err);
        };

        AppConsts.appBaseHref = this.getBaseHref();
        const appBaseUrl = this.getDocumentOrigin() + AppConsts.appBaseHref;
        this.getApplicationConfig(appBaseUrl, () => {
          this.getUserConfiguration(() => {
            abp.event.trigger('abp.dynamicScriptsInitialized');
            // do not use constructor injection for AppSessionService
            const appSessionService = this._injector.get(AppSessionService);
            appSessionService.init().then(
              (result) => {
                abp.ui.clearBusy();
                if (this.shouldLoadLocale()) {
                  const angularLocale = this.convertAbpLocaleToAngularLocale(
                    abp.localization.currentLanguage.name
                  );
                  import(`/node_modules/@angular/common/locales/${angularLocale}.mjs`).then(
                    (module) => {
                      registerLocaleData(module.default);
                      resolve(result);
                    },
                    reject
                  );
                } else {
                  resolve(result);
                }
              },
              (err) => {
                abp.ui.clearBusy();
                reject(err);
              }
            );
          }, rejectInitialization);
        }, rejectInitialization);
      });
    };
  }

  private getBaseHref(): string {
    const baseUrl = this._platformLocation.getBaseHrefFromDOM();
    if (baseUrl) {
      return baseUrl;
    }

    return '/';
  }

  private getDocumentOrigin(): string {
    if (!document.location.origin) {
      const port = document.location.port ? ':' + document.location.port : '';
      return (
        document.location.protocol + '//' + document.location.hostname + port
      );
    }

    return document.location.origin;
  }

  private shouldLoadLocale(): boolean {
    return (
      abp.localization.currentLanguage.name &&
      abp.localization.currentLanguage.name !== 'en-US'
    );
  }

  private convertAbpLocaleToAngularLocale(locale: string): string {
    if (!AppConsts.localeMappings) {
      return locale;
    }

    const localeMapings = _filter(AppConsts.localeMappings, { from: locale });
    if (localeMapings && localeMapings.length) {
      return localeMapings[0]['to'];
    }

    return locale;
  }

  private getCurrentClockProvider(
    currentProviderName: string
  ): abp.timing.IClockProvider {
    if (currentProviderName === 'unspecifiedClockProvider') {
      return abp.timing.unspecifiedClockProvider;
    }

    if (currentProviderName === 'utcClockProvider') {
      return abp.timing.utcClockProvider;
    }

    return abp.timing.localClockProvider;
  }

  private getUserConfiguration(callback: () => void, errorCallback: (err: any) => void, syncUserLanguage = true): void {
    let cookieLangValue = abp.utils.getCookieValue(
      'Abp.Localization.CultureName'
    );
    const hasLanguageCookie = !!cookieLangValue;

    if (!cookieLangValue) {
      cookieLangValue = 'vi';
    }

    const token = abp.auth.getToken();

    const requestHeaders: { [key: string]: string } = {
      'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
      '.AspNetCore.Culture': `c=${cookieLangValue}|uic=${cookieLangValue}`,
    };

    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    this._httpClient
      .get<any>(
        `${AppConsts.remoteServiceBaseUrl}/AbpUserConfiguration/GetAll`,
        {
          headers: requestHeaders,
        }
      )
      .subscribe({
        next: (response) => {
          const result = response.result;

          if (
            syncUserLanguage &&
            token &&
            hasLanguageCookie &&
            this.shouldSyncUserLanguage(cookieLangValue, result)
          ) {
            this.changeUserLanguage(
              cookieLangValue,
              requestHeaders,
              () => this.getUserConfiguration(callback, errorCallback, false),
              () => {
                this.applyUserConfiguration(result);
                callback();
              }
            );
            return;
          }

          this.applyUserConfiguration(result);
          callback();
        },
        error: errorCallback,
      });
  }

  private shouldSyncUserLanguage(languageName: string, userConfiguration: any): boolean {
    const currentLanguageName = userConfiguration?.localization?.currentLanguage?.name;

    if (!currentLanguageName || currentLanguageName === languageName) {
      return false;
    }

    const languages = userConfiguration?.localization?.languages;
    if (!Array.isArray(languages)) {
      return true;
    }

    return languages.some((language) => language.name === languageName && !language.isDisabled);
  }

  private changeUserLanguage(
    languageName: string,
    requestHeaders: { [key: string]: string },
    successCallback: () => void,
    errorCallback: () => void
  ): void {
    this._httpClient
      .post<any>(
        `${AppConsts.remoteServiceBaseUrl}/api/services/app/User/ChangeLanguage`,
        { languageName },
        {
          headers: {
            ...requestHeaders,
            'Content-Type': 'application/json-patch+json',
          },
        }
      )
      .subscribe(successCallback, errorCallback);
  }

  private applyUserConfiguration(result: any): void {
    _merge(abp, result);

    abp.clock.provider = this.getCurrentClockProvider(
      result.clock.provider
    );

    moment.locale(abp.localization.currentLanguage.name);

    if (abp.clock.provider.supportsMultipleTimezone) {
      moment.tz.setDefault(abp.timing.timeZoneInfo.iana.timeZoneId);
    }
  }

  private getApplicationConfig(appRootUrl: string, callback: () => void, errorCallback: (err: any) => void) {
    this._httpClient
      .get<any>(`${appRootUrl}assets/${environment.appConfig}`, {
        headers: {
          'Abp.TenantId': `${abp.multiTenancy.getTenantIdCookie()}`,
        },
      })
      .subscribe({
        next: (response) => {
          AppConsts.appBaseUrl = response.appBaseUrl;
          AppConsts.remoteServiceBaseUrl = response.remoteServiceBaseUrl;
          AppConsts.localeMappings = response.localeMappings;

          // Find tenant from subdomain
          const tenancyName = this.resolveTenancyName(response.appBaseUrl);

          if (tenancyName == null) {
            callback();
          } else {
            this.ConfigureTenantIdCookie(tenancyName, callback, errorCallback);
          }
        },
        error: errorCallback,
      });
  }

  private ConfigureTenantIdCookie(tenancyName: string, callback: () => void, errorCallback: (err: any) => void) {
    const accountServiceProxy: AccountServiceProxy = this._injector.get(AccountServiceProxy);
    const input = new IsTenantAvailableInput();
    input.tenancyName = tenancyName;

    accountServiceProxy.isTenantAvailable(input).subscribe({
      next: (result: IsTenantAvailableOutput) => {
        if (result.state === TenantAvailabilityState._1) { // Available
          abp.multiTenancy.setTenantIdCookie(result.tenantId);
        }

        callback();
      },
      error: errorCallback,
    });
  }

  private resolveTenancyName(appBaseUrl): string | null {
    const subdomainTenantResolver = new SubdomainTenantResolver();
    const tenancyName = subdomainTenantResolver.resolve(appBaseUrl);
    if (tenancyName) {
      return tenancyName;
    }

    // add other tenancy resolvers here, ex: CookieTenantResolver, QueryStringTenantResolver etc...

    return null;
  }
}
